import { useEffect } from "react";
import {
  FALLBACK_TEXT,
  normalizeError,
  refreshPage,
  SUPPORT_EMAIL,
} from "./errorUtils";
import { hideInitialLoadingScreen } from "contexts/globalLoadingScreen";

interface PureHtmlFallbackProps {
  error?: unknown;
}

/** Dependency-free error fallback used when the main error view cannot render. */
export const PureHtmlFallback = ({ error }: PureHtmlFallbackProps) => {
  const normalizedError = normalizeError(error);
  console.error(normalizedError);

  useEffect(() => {
    try {
      hideInitialLoadingScreen();
    } catch (e) {
      console.error(e);
      // Error recovery remains available if the loading screen cannot be hidden.
    }
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#fafafa",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          textAlign: "center",
          padding: "48px 32px",
          maxWidth: 420,
          backgroundColor: "#fff",
          borderRadius: 12,
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
          border: "1px solid #e0e0e0",
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            margin: "0 auto 24px",
            borderRadius: "50%",
            backgroundColor: "#ffebee",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 32,
          }}
          role="img"
          aria-label="Warning"
        >
          {"\u26A0\uFE0F"}
        </div>

        <h1
          style={{
            margin: "0 0 16px",
            fontSize: 24,
            fontWeight: 700,
            color: "#212121",
          }}
        >
          {FALLBACK_TEXT.globalErrorTitle}
        </h1>

        <p
          style={{
            margin: "0 0 32px",
            fontSize: 15,
            lineHeight: 1.6,
            color: "#666",
          }}
        >
          {FALLBACK_TEXT.globalErrorDescription}
        </p>

        <button
          type="button"
          onClick={refreshPage}
          style={{
            width: "100%",
            padding: "12px 20px",
            marginBottom: 16,
            fontSize: 14,
            fontWeight: 600,
            color: "#fff",
            // Brand primary hardcoded: this fallback imports nothing, so it can't read the theme.
            backgroundColor: "#4441C8",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          {FALLBACK_TEXT.globalErrorRefresh}
        </button>

        <p
          style={{
            marginTop: 16,
            fontSize: 13,
            color: "#888",
          }}
        >
          {FALLBACK_TEXT.globalErrorContactSupport}:{" "}
          <span style={{ color: "#4441C8" }}>{SUPPORT_EMAIL}</span>
        </p>
      </div>
    </div>
  );
};
